import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export const CREDIT_COSTS: Record<string, number> = {
  'video-prompt': 2,
  'ad-copy': 1,
  'marketing-plan': 3,
  'niche-strategy': 3,
  'launch-strategy': 4,
};


export const useAICredits = () => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<number | null>(null);
  const [monthlyAllowance, setMonthlyAllowance] = useState(0);
  const [totalUsed, setTotalUsed] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.rpc('get_ai_credits', {
        p_user_id: user.id,
      });

      if (error) {
        console.error('Error fetching credits:', error);
        return;
      }

      if (data) {
        const credits = data as { balance: number; monthly_allowance: number; total_used: number };
        setBalance(credits.balance);
        setMonthlyAllowance(credits.monthly_allowance);
        setTotalUsed(credits.total_used);
      }
    } catch (e) {
      console.error('Failed to fetch credits:', e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  const refreshCredits = useCallback(() => {
    fetchCredits();
  }, [fetchCredits]);

  const updateBalanceFromHeader = useCallback((newBalance: number) => {
    setBalance(newBalance);
  }, []);

  return {
    balance,
    monthlyAllowance,
    totalUsed,
    loading,
    refreshCredits,
    updateBalanceFromHeader,
  };
};
